import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { customerAPI } from '../services/api';
import { useTranslation } from '../i18n';
import { ArrowLeft, Package, Clock, MapPin, User as UserIcon } from 'lucide-react';

const STATUS_COLORS = {
  draft:     { bg: 'rgba(165,170,190,0.15)', color: '#a5aabe', label: { ka: 'მუშავდება', en: 'Processing' } },
  stored:    { bg: 'rgba(255,176,32,0.15)',  color: '#ffb020', label: { ka: 'ბოქსში',    en: 'In locker' } },
  picked_up: { bg: 'rgba(139,198,62,0.15)',  color: '#8BC63E', label: { ka: 'გატანილია', en: 'Picked up' } },
  expired:   { bg: 'rgba(255,68,102,0.15)',  color: '#ff4466', label: { ka: 'ვადაგასული', en: 'Expired' } },
};

export default function MyOrders() {
  const [parcels, setParcels] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { t, isGeorgian } = useTranslation();

  useEffect(() => {
    customerAPI.myParcels().then(r => {
      setParcels(r.data || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const sizeLabel = (size) => {
    if (size === 'S') return isGeorgian ? 'პატარა' : 'Small';
    if (size === 'M') return isGeorgian ? 'საშუალო' : 'Medium';
    if (size === 'L') return isGeorgian ? 'დიდი' : 'Large';
    return size || '—';
  };

  return (
    <div style={s.page}>
      <header style={s.header}>
        <div style={s.back} onClick={() => navigate('/home')}><ArrowLeft size={18} /> {t('common.back')}</div>
        <span style={s.headerTitle}>{isGeorgian ? 'ჩემი შეკვეთები' : 'My Parcels'}</span>
        <div style={{ width: 60 }} />
      </header>

      <div style={s.content}>
        {loading ? (
          <div style={s.empty}>{t('common.loading')}</div>
        ) : parcels.length === 0 ? (
          <div style={s.empty}>
            <Package size={40} color="#8a8da4" />
            <p style={{ marginTop: 12 }}>
              {isGeorgian ? 'ამანათები ჯერ არ გაქვთ' : 'No parcels yet'}
            </p>
            <button style={s.btn} onClick={() => navigate('/home')}>
              {isGeorgian ? 'გააგზავნე პირველი ამანათი' : 'Send your first parcel'}
            </button>
          </div>
        ) : (
          parcels.map(p => {
            const statusMeta = STATUS_COLORS[p.status] || STATUS_COLORS.draft;
            return (
              <div key={p.id} style={s.orderCard}>
                <div style={s.orderTop}>
                  <span style={s.orderNo}>{p.order_no}</span>
                  <span style={{ ...s.badge, background: statusMeta.bg, color: statusMeta.color }}>
                    {statusMeta.label[isGeorgian ? 'ka' : 'en']}
                  </span>
                </div>

                <div style={s.orderRow}>
                  <MapPin size={13} color="#8789a0" />
                  <span>
                    {p.device_name || 'Locker'} · {isGeorgian ? 'ბოქსი' : 'Box'} {p.box_name} · <b>{sizeLabel(p.box_size)}</b>
                  </span>
                </div>

                {p.recipient_name && (
                  <div style={s.orderRow}>
                    <UserIcon size={13} color="#8789a0" />
                    <span>{isGeorgian ? 'მიმღები' : 'Recipient'}: {p.recipient_name}</span>
                  </div>
                )}

                <div style={s.orderRow}>
                  <Clock size={13} color="#8789a0" />
                  <span>{new Date(p.created_at).toLocaleString(isGeorgian ? 'ka-GE' : 'en-US')}</span>
                </div>

                {p.description && (
                  <div style={s.orderDesc}>{p.description}</div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

const s = {
  page: { minHeight: '100vh', background: '#07080c', color: '#e4e5ed' },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 24px', borderBottom: '1px solid #252733' },
  back: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#8789a0', cursor: 'pointer' },
  headerTitle: { fontSize: 15, fontWeight: 600 },
  content: { maxWidth: 520, margin: '0 auto', padding: '16px 16px 40px' },
  empty: { textAlign: 'center', padding: '60px 20px', color: '#8a8da4' },
  btn: { marginTop: 16, padding: '12px 24px', background: '#8BC63E', color: '#07080c', border: 'none', borderRadius: 10, fontWeight: 700, fontSize: 14, cursor: 'pointer' },
  orderCard: { background: '#0f1117', border: '1px solid #252733', borderRadius: 12, padding: 16, marginBottom: 12 },
  orderTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 },
  orderNo: { fontSize: 11, color: '#8a8da4', fontFamily: "'Space Mono', monospace", letterSpacing: 0.5 },
  badge: { fontSize: 10, fontWeight: 700, padding: '4px 10px', borderRadius: 6, textTransform: 'uppercase', letterSpacing: 0.5 },
  orderRow: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#e4e5ed', marginBottom: 6 },
  orderDesc: { marginTop: 10, padding: '8px 10px', background: '#1a1f36', borderRadius: 6, fontSize: 12, color: '#8789a0' },
};
